import { Button, ClickAwayListener, Grow, Icon, Popper, TextField, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import PillButton from '../button/PillButton';
import { DATE_FORMAT_DEFAULT_NO_TIME } from '../../../appConstants';
import DateRangePicker from './DateRange';

interface IDateFilterProps {
    handleDateRangeChange : (start : moment.Moment | null, end : moment.Moment | null, changedBy ?: 'start' | 'end') => void;
    onApplyClick : () => void;
    closeButtonTitle ?: string;
    applyButtonTitle ?: string;
    enableCloseButton ?: boolean;
    enableApplyButton ?: boolean;
    selectedFromDate : moment.Moment;
    selectedToDate : moment.Moment;
    className ?: string;
}

/**
 * @param handleDateRangeChange - Function that handles changes to the selected date range. It accepts the start and end dates as moment.Moment objects or null, and an optional parameter indicating which part of the range ('start' or 'end') triggered the change. Required.
 * @param onApplyClick - Callback function that is called when the apply button is clicked. This is typically used to apply the selected date range to a filter or dataset. Required.
 * @param closeButtonTitle - Optional title for the close button, which can be used to provide a tooltip or label.
 * @param applyButtonTitle - Optional title for the apply button, which can be used to provide a tooltip or label.
 * @param enableCloseButton - Optional boolean that determines if the close button should be displayed and enabled. This can be used to provide control over user interaction with the dialog or component.
 * @param enableApplyButton - Optional boolean that determines if the apply button should be displayed and enabled. This allows for conditional activation based on certain criteria, such as date range validity.
 * @param selectedFromDate - The currently selected starting date, represented as a moment.Moment object. Required.
 * @param selectedToDate - The currently selected ending date, represented as a moment.Moment object. Required.
 * @param className - Optional custom CSS class to apply additional styling to the date filter component.
 * 
 * @return {React.ReactElement} The React element for the Date Filter component.
 */
const DateFilter = (props : IDateFilterProps) : React.ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { 
        selectedFromDate,
        selectedToDate,
        closeButtonTitle,
        applyButtonTitle,
        enableCloseButton,
        enableApplyButton
    } = props;

    const anchorEl = React.useRef(null);

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const handleClick = () : void => {
        setIsOpen(!isOpen);
    };

    const handleClose = (event : MouseEvent | TouchEvent) : void => {
        event.preventDefault();
        setIsOpen(false);
    };

    const onCancelClick = () : void => {
        setIsOpen(false);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isSelectedDateRangeInvalid = useMemo<boolean>(() => {
        let days = 0;

        const start = { ...selectedFromDate };
        const end = { ...selectedToDate };
        const fromDate = moment(start).startOf('day');
        const toDate = moment(end).startOf('day');

        days = Math.round(moment.duration(toDate.diff(fromDate)).asDays());

        return (days > 365) ? true : false;
        
    }, [selectedFromDate, selectedToDate]); 

    const filterComponent = useMemo<React.ReactElement>(() => {
        return (
            <Popper className={'zi2000 pr10'} anchorEl={anchorEl.current} open={isOpen} transition>
                {({ TransitionProps, placement }) : JSX.Element => (
                    <Grow className={'mt8 filterShadow bcw'}
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <div className={'zi1000 p10 filterShadow'}>
                            <form>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <div className={'fdc'}>
                                        <div className='fdr'>
                                            <DateRangePicker
                                                className={'jcfe aife flx1'}
                                                onChange={props.handleDateRangeChange}
                                                start={selectedFromDate}
                                                end={selectedToDate}
                                                positionLeft={true}
                                            />
                                        </div>
                                        <div className={'fdr flx1 pt10 pb10 pr10'}>
                                            <div className={'flx1'}/>
                                            <Button
                                                className={enableCloseButton === false ? 'dn' : 'fwb h35'}
                                                onClick={onCancelClick}>
                                                {props.closeButtonTitle ? closeButtonTitle : 'Cancel'}
                                            </Button>
                                            <Tooltip title={`${isSelectedDateRangeInvalid ? 'Date range cannot be more then 365 days' : ''}`}>
                                                <div>
                                                    <PillButton
                                                        disabled={isSelectedDateRangeInvalid}
                                                        text={applyButtonTitle ? applyButtonTitle : 'Apply'}
                                                        className={enableApplyButton === false ? 'dn' : 'ml15 pl20 pr20 h35 cpd'}
                                                        onClick={() : void => {
                                                            props.onApplyClick();
                                                            setIsOpen(false);
                                                        }}
                                                        color={'secondary'}
                                                        size={'small'}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </ClickAwayListener>
                            </form>
                        </div>
                    </Grow>
                )}
            </Popper>
        );
    }, [anchorEl, isOpen, closeButtonTitle, applyButtonTitle, enableCloseButton, enableApplyButton, selectedFromDate, selectedToDate]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={`fdr jcfe ${props.className ? props.className : ''}`}>
            <TextField
                ref={anchorEl}
                className={'w250'}
                label={'Filter By Date'}
                aria-haspopup='true'
                name={'filter'}
                value={props.selectedFromDate.local().format(DATE_FORMAT_DEFAULT_NO_TIME) + ' - ' + (props.selectedToDate.local().format(DATE_FORMAT_DEFAULT_NO_TIME))}
                variant={'standard'}
                onClick={handleClick}
                InputProps={{
                    endAdornment: (
                        <Icon fontSize='small'>
                        filter_list
                        </Icon>
                    ),
                }}
            />
            {filterComponent}
        </div>
    );
};

export default DateFilter;